
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchAgentVersions, rollbackToVersion } from "@/services/admin/agentService";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { AgentVersion } from "@/types/admin";
import { HistoryIcon, RotateCcw, Eye } from "lucide-react";

interface AgentVersionsProps {
  agentId: string;
  onVersionRestored: () => void;
}

export const AgentVersions = ({ agentId, onVersionRestored }: AgentVersionsProps) => {
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AgentVersion | null>(null);

  const {
    data: versions,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["agent-versions", agentId],
    queryFn: () => fetchAgentVersions(agentId),
    enabled: !!agentId,
  });

  const rollbackMutation = useMutation({
    mutationFn: (versionId: string) => rollbackToVersion(agentId, versionId),
    onSuccess: () => {
      toast({
        title: "Version restored",
        description: "Agent has been rolled back to the selected version",
      });
      refetch();
      onVersionRestored();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to restore version: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const handlePreviewJson = (version: AgentVersion) => {
    setSelectedVersion(version);
    setShowJsonDialog(true);
  };

  const handleRollback = (versionId: string) => {
    if (confirm("Are you sure you want to restore this version?")) {
      rollbackMutation.mutate(versionId);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (!isNaN(date.getTime())) {
        return formatDistanceToNow(date, { addSuffix: true });
      }
      return "Unknown";
    } catch (error) {
      return "Unknown";
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
    </div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">
      Error loading versions: {error instanceof Error ? error.message : "Unknown error"}
    </div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <HistoryIcon className="h-4 w-4" />
        <h3 className="text-lg font-medium">Version History</h3>
      </div>
      
      {versions && versions.length > 0 ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Edited By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell className="font-medium">v{version.version_number}</TableCell>
                  <TableCell>{version.edited_by || "System"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatTimeAgo(version.created_at)}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handlePreviewJson(version)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRollback(version.id)}
                      disabled={rollbackMutation.isPending}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center p-4 border rounded-md bg-muted/50">
          <p>No version history available</p>
        </div>
      )}

      {selectedVersion && (
        <Dialog open={showJsonDialog} onOpenChange={setShowJsonDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Version {selectedVersion.version_number} Settings</DialogTitle>
              <DialogDescription>
                Created {formatTimeAgo(selectedVersion.created_at)} by {selectedVersion.edited_by || "System"}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[400px] rounded-md border p-4">
              <pre className="text-xs font-mono">
                {JSON.stringify(selectedVersion.settings_snapshot, null, 2)}
              </pre>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
